import styled from "@emotion/styled"
import { PButtonPure, PHeading, PLink, PScroller, PText } from "@porsche-design-system/components-react"
import {
	breakpoint,
	colorContrastLower,
	colorFrosted,
	colorInfo,
	durationMd,
	easeInOut,
	spacingStatic2Xs,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { NotificationListItem } from "@slfinrtl/ui-bff-routes"
import { useCallback } from "react"
import type { MouseEvent } from "react"
import { defineMessages, FormattedMessage } from "react-intl"
import { Link } from "react-router"
import type { LinkProps } from "react-router"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { isListItemLazy } from "src/common/isListItemLazy/isListItemLazy"
import { commonMessages } from "src/common/messages/common.messages"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { NewBadge, PictureStatic } from "src/components"
import type { PictureStaticProps, PictureStaticSize } from "src/components"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"
import { NotificationListItemMetaTag } from "./NotificationListItemMetaTag"

const messages = defineMessages({
	markAsRead: {
		defaultMessage: "Mark as read",
		id: "notificationList.markAsRead",
	},
	markAsUnread: {
		defaultMessage: "Mark as unread",
		id: "notificationList.markAsUnread",
	},
	noNotificationsHeadline: {
		defaultMessage: "There are currently no notifications or tasks.",
		id: "notificationList.noNotificationsHeadline",
	},
	openDetails: {
		defaultMessage: "Open Details",
		id: "notificationList.openDetails",
	},
	readTimeAgo: {
		defaultMessage: "Read {value}",
		id: "notificationList.readTimeAgo",
	},
})

export interface NotificationListProps {
	className?: string
	emptyListMessage?: string
	items: NotificationListItem[]

	compact?: boolean
	isPending: (id: string) => boolean
	onNotificationClick?: (e: MouseEvent<HTMLElement>) => void
	updateNotificationStatus: (id: string, newStatus: "READ" | "SEEN") => void
}

const editStageMap: Partial<Record<NotificationListItem["notificationTypeKey"], VehicleDetailsStage>> = {
	"InventoryService.missingFirstRegistrationDate": "conditionService",
}

export const NotificationList = ({
	className,
	compact,
	emptyListMessage,
	isPending,
	items,
	onNotificationClick,
	updateNotificationStatus,
}: NotificationListProps) => {
	const routes = useDealerAppRoutes()

	const lastOpenIndex = items.findIndex(
		(_, i) => items[i].dealerAppStatus === "OPEN" && items.at(i + 1)?.dealerAppStatus !== "OPEN",
	)

	const onMarkAsReadClick = useCallback(
		(id: string) => () =>
			updateNotificationStatus(
				id,
				items.find((item) => item.userNotificationId === id)!.dealerAppStatus === "READ" ? "SEEN" : "READ",
			),
		[items, updateNotificationStatus],
	)

	const getLinkProps: ((item: NotificationListItem) => LinkProps | undefined) | undefined = useCallback(
		(item: NotificationListItem) => {
			if (routes?.inventoryList?.vehicleDetails && item.references.inventoryVehicleId) {
				return {
					state: { editStage: editStageMap[item.notificationTypeKey] },
					to: routes.inventoryList.vehicleDetails.url(item.references.inventoryVehicleId),
				}
			}
			if (routes?.salesList?.saleDetails && item.references.saleId) {
				return { to: routes.salesList.saleDetails.url(item.references.saleId) }
			}
			return undefined
		},
		[routes],
	)

	return (
		<Wrapper className={className}>
			{items.length === 0 ? (
				<Center>
					<PHeading size="sm">
						{emptyListMessage ?? <FormattedMessage {...messages.noNotificationsHeadline} />}
					</PHeading>
				</Center>
			) : (
				items.map((item, index) => {
					const linkProps = getLinkProps(item)
					const isLastOpen = lastOpenIndex < 0 ? false : index === lastOpenIndex + 1 // add 1 since the background is added to the top of the following item
					const isKindInfo = item.kind === "info"
					const isStatusRead = item.dealerAppStatus === "READ"

					const elementModelName = (
						<PHeading size={compact ? "sm" : "md"} weight="semibold" tag="h3" ellipsis>
							{item.modelName}
						</PHeading>
					)

					const elementTimeAgo = (
						<PText size="xs" color="contrast-medium">
							{isStatusRead ? (
								<FormattedMessage
									{...messages.readTimeAgo}
									values={{ value: item.dealerAppStatusLastUpdate }}
								/>
							) : (
								item.createdTimeAgo
							)}
						</PText>
					)

					return (
						<LayoutMessage $isLastOpen={isLastOpen} key={item.userNotificationId} $compact={compact}>
							{isLastOpen && <DividerNewBadge icon="arrow-up" compact={compact} />}
							<ImageHeadingTagsWrapper>
								<NotificationListItemImage
									compact={compact}
									picture={item.image}
									alt={item.modelName}
									lazy={isListItemLazy(index)}
								/>

								<HeadingTagsWrapper $compact={compact}>
									<HeadingDateWrapper>
										{linkProps ? (
											<CardLink {...linkProps} onClick={onNotificationClick}>
												{elementModelName}
											</CardLink>
										) : (
											elementModelName
										)}
										{compact && elementTimeAgo}
									</HeadingDateWrapper>

									{item.metaMessages && (
										<PScroller>
											{item.metaMessages.map((msg) => (
												<NotificationListItemMetaTag
													compact={compact}
													key={`${msg.key}-${msg.message}`}
													message={msg.message}
													metaMessageKey={msg.key}
													canBeCopied={msg.canBeCopied}
													notificationTypeKey={item.notificationTypeKey}
													severity={item.severity}
												/>
											))}
										</PScroller>
									)}
								</HeadingTagsWrapper>
							</ImageHeadingTagsWrapper>

							{!compact && (
								<MessagesWrapper>
									<OnTopText>{item.message}</OnTopText>
									{elementTimeAgo}
								</MessagesWrapper>
							)}

							<ActionsCell $compact={compact}>
								{linkProps && !compact && (
									<PLink
										icon={isKindInfo ? "arrow-right" : "edit"}
										compact
										variant={item.kind === "task" ? "primary" : "secondary"}
										onClick={onNotificationClick}
									>
										<Link {...linkProps}>
											<FormattedMessage
												{...(isKindInfo ? messages.openDetails : commonMessages.edit)}
											/>
										</Link>
									</PLink>
								)}

								{isKindInfo && (
									<MarkAsReadButton
										icon={isStatusRead ? "success-filled" : "success"}
										hideLabel={compact}
										size="xs"
										loading={isPending(item.userNotificationId)}
										onClick={onMarkAsReadClick(item.userNotificationId)}
									>
										<FormattedMessage
											{...(isStatusRead ? messages.markAsUnread : messages.markAsRead)}
										/>
									</MarkAsReadButton>
								)}
							</ActionsCell>
						</LayoutMessage>
					)
				})
			)}
		</Wrapper>
	)
}

const Wrapper = styled.section`
	display: flex;
	flex-direction: column;
`

const Center = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${spacingStaticSm};
	padding: ${spacingStaticLg} 0;
`

const LayoutMessage = styled("div", { shouldForwardProp })<{ $compact?: boolean; $isLastOpen?: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
	padding: ${({ $compact }) => ($compact ? spacingStaticSm : spacingStaticMd)};

	@media (min-width: ${breakpoint.s}px) {
		display: grid;
		grid-template-columns: minmax(0, 1fr) ${({ $compact }) => ($compact ? "min-content" : "200px")};
		gap: ${spacingStaticMd};
	}
	
	&:not(:first-of-type)::before {
		content: "";
		position: absolute;
		top: 0;
		height: 1px;
		inset-inline: 0;
		background: ${({ $isLastOpen }) => ($isLastOpen ? colorInfo : colorContrastLower)};
	}

	@media (hover: hover) and (pointer: fine) {
        transition: background-color ${durationMd} ${easeInOut};
        &:hover {
			background: ${colorFrosted};
        }
    }
`

const ImageHeadingTagsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};

	@media (min-width: ${breakpoint.s}px) {
		flex-direction: row;
        gap: ${spacingStaticMd};
        align-items: center;
    }
`

const HeadingTagsWrapper = styled("div", { shouldForwardProp })<{ $compact?: boolean }>`
	display: flex;
	flex-direction: column;
	gap: ${({ $compact }) => ($compact ? spacingStatic2Xs : spacingStaticSm)};
	min-width: 0;
`

const HeadingDateWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: baseline;
	gap: ${spacingStaticSm};
	white-space: nowrap;
`

const NotificationListItemImage = styled(
	({ compact, ...rest }: Omit<PictureStaticProps, "size"> & { compact?: boolean }) => {
		const size: PictureStaticSize = compact ? 160 : 240
		return <PictureStatic size={size} {...rest} />
	},
)`
	@media (min-width: ${breakpoint.s}px) {
		min-width: ${({ compact }) => (compact ? 80 : 120)}px;
		width: ${({ compact }) => (compact ? 80 : 120)}px;
	}
`

const MessagesWrapper = styled.div`
	grid-column: 1;
`

const OnTop = styled.div`
	position: relative;
	z-index: 2; /* to be on top of link with pseudo element */
`

const OnTopText = OnTop.withComponent(PText)

const ActionsCell = styled("div", { shouldForwardProp })<{ $compact?: boolean }>`
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
	align-items: start;

	@media (min-width: ${breakpoint.s}px) {
		grid-column: 2;
		grid-row: ${({ $compact }) => ($compact ? "1" : "1 / span 2")};
		align-self: stretch;
		justify-content: center;
	}
`

const CardLink = styled(Link)`
	min-width: 0;
	text-decoration: none !important;

	&::before {
		content: "";
		position: absolute;
		inset: 0;
	}
`

const MarkAsReadButton = styled(PButtonPure)`
	align-self: start;
`

const DividerNewBadge = styled(NewBadge)`
	position: absolute;
	top: 0;
	left: 50%;
	transform: translateX(-50%) translateY(-50%);
	z-index: 10;
`
